import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Star, Loader2 } from "lucide-react";
import { useGroups, GROUP_PLAN_LIMIT_PREFIX } from "@core/hooks/useGroups";
import { useQuotaModal } from "@core/contexts/QuotaModalContext";
import { useToast } from "@core/hooks/use-toast";

interface SaveEmailsAsGroupCtaProps {
  emails: string[];
  onSaved?: (groupId: string) => void;
  className?: string;
}

/**
 * CTA discret : convertit une liste d'e-mails saisie une fois (audience
 * gratuite "e-mails directs") en groupe réutilisable (fonctionnalité
 * payante). Monté après création du sondage et sur le panneau de suivi des
 * invitations — les deux moments où la liste vient d'être utile.
 */
export const SaveEmailsAsGroupCta: React.FC<SaveEmailsAsGroupCtaProps> = ({
  emails,
  onSaved,
  className,
}) => {
  const { t } = useTranslation(["polls", "creator", "common"]);
  const { createGroup } = useGroups();
  const { openQuotaModal } = useQuotaModal();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  if (!emails || emails.length === 0) return null;

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const group = await createGroup({
        name: name.trim(),
        member_emails: emails,
      });
      if (group) {
        toast({
          title: t("polls:groups.group_saved_title"),
          description: t("polls:groups.group_saved_description", {
            name: group.name,
            count: group.member_emails.length,
          }),
        });
        setOpen(false);
        setName("");
        onSaved?.(group.id);
      }
    } catch (err) {
      const message = (err as Error).message || "";
      if (message.startsWith(GROUP_PLAN_LIMIT_PREFIX)) {
        openQuotaModal("plan_limit", "date");
      } else {
        toast({
          title: t("polls:groups.error_title"),
          description: message,
          variant: "destructive",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="save-emails-as-group-cta"
        className={`inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline ${className ?? ""}`}
      >
        <Star className="w-3.5 h-3.5" />
        {t("creator:save_as_group_cta", { count: emails.length })}
      </button>
    );
  }

  return (
    <div
      className={`flex flex-col sm:flex-row gap-2 items-stretch sm:items-center ${className ?? ""}`}
    >
      <input
        type="text"
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleSave()}
        placeholder={t("creator:save_as_group_name_placeholder")}
        className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      />
      <div className="flex gap-2 shrink-0">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !name.trim()}
          data-testid="save-emails-as-group-confirm"
          className="px-3 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 whitespace-nowrap flex items-center gap-1.5"
        >
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {t("common:save")}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          disabled={saving}
          className="px-3 py-2 text-sm font-medium rounded-lg text-foreground hover:bg-accent"
        >
          {t("common:cancel")}
        </button>
      </div>
    </div>
  );
};
